/**
 * Cart Drawer Module
 * Handles slide-out cart with care package grouping
 */
(function() {
  'use strict';

  // DOM Elements
  const drawer = document.getElementById('cart-drawer');
  const content = document.getElementById('cart-drawer-content');
  const emptyState = document.getElementById('cart-drawer-empty');
  const footer = document.getElementById('cart-drawer-footer');
  const totalEl = document.getElementById('cart-drawer-total');
  const originalTotalEl = document.getElementById('cart-drawer-original-total');
  const savingsEl = document.getElementById('cart-drawer-savings');
  const checkoutBtn = document.getElementById('cart-drawer-checkout');
  const upsellsContainer = document.getElementById('cart-drawer-upsells');
  const upsellsList = document.getElementById('cart-drawer-upsells-list');
  const upsellsDataEl = document.getElementById('cart-drawer-upsells-data');
  const restrictionsEl = document.getElementById('cart-drawer-restrictions');
  const checkoutBtnEl = document.getElementById('cart-drawer-checkout');

  if (!drawer) return;

  // Prevent initial flash: keep hidden in HTML until JS hydrates, then force a closed baseline state
  drawer.classList.remove('is-open');
  drawer.setAttribute('aria-hidden', 'true');
  drawer.hidden = false;
  document.body.style.overflow = '';

  let isEnforcing = false;
  let isRunningCartRules = false;
  let pendingRestrictionMsg = null;
  // Handle for the restriction banner's auto-fade. Kept at module scope so a
  // re-render, a pause, or a manual dismiss can always cancel the one timer in
  // flight instead of letting several pile up on the same banner.
  let restrictionFadeTimer = null;
  // Signature of the last discount-code set we cleared for the member discount,
  // so a clear that doesn't stick can't re-fire every render (see
  // enforceMembershipDiscountCodes).
  let lastClearedCodeSig = null;

  // Load upsell products from JSON data
  let upsellProducts = [];
  if (upsellsDataEl) {
    try {
      upsellProducts = JSON.parse(upsellsDataEl.textContent);
    } catch (e) {
      console.error('Error parsing upsell data:', e);
    }
  }

  // Dynamic upsells: fetched from the most recently added animal/bundle's
  // pinned_upsells metafield via /products/{handle}?view=cart-upsells.
  // Cache stores a Promise per handle so concurrent calls dedupe.
  const DYNAMIC_UPSELL_LIMIT = 3;
  const dynamicUpsellsCache = new Map();

  // GiveAFriend subscription opt-in (see snippets/gaf-subscription-optin.liquid).
  // The single property the whole offer hangs on: it sits on the subscription
  // line only, holding the flat dollars-off amount as a string, e.g. "10". The
  // Every Possible Discount rule matches this key and value on any one line and
  // takes that much off the order total — nothing is stamped on the other lines.
  const GAF_SUBSCRIPTION_PROPERTY = '_gaf_subscription';

  function isGafSubscription(item) {
    return !!(item && item.properties && item.properties[GAF_SUBSCRIPTION_PROPERTY]);
  }

  // Discount CODES currently on the cart (e.g. "LOVELY"). Automatic and app
  // discounts — including the GiveAFriend member discount — are never codes
  // (their application type is 'automatic' / 'script'), so they never appear
  // here and are never cleared. Reads every shape cart.js exposes codes in and
  // tolerates any of them being absent, so an unexpected payload yields [] (a
  // no-op) rather than a wrong action.
  function getAppliedDiscountCodes(cartData) {
    if (!cartData) return [];
    const codes = [];
    const add = function (code) {
      if (code && codes.indexOf(code) === -1) codes.push(code);
    };
    (cartData.cart_level_discount_applications || []).forEach(function (app) {
      if (app && app.type === 'discount_code') add(app.title);
    });
    (cartData.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (alloc) {
        const app = alloc && alloc.discount_application;
        if (app && app.type === 'discount_code') add(app.title);
      });
    });
    (cartData.discount_codes || []).forEach(function (dc) {
      if (dc) add(dc.code);
    });
    return codes;
  }

  function getItemHandle(item) {
    if (!item) return null;
    if (item.handle) return item.handle;
    // Fallback: extract from URL like "/products/panda-bear?variant=..."
    const url = item.url || '';
    const match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function isAnimalOrBundle(item) {
    const ptype = (item && item.product_type) || '';
    return ptype.indexOf('Animal') !== -1 || ptype === 'Bundle';
  }

  function findMostRecentAnimalOrBundle(cartData) {
    if (!cartData || !cartData.items) return null;
    for (let i = cartData.items.length - 1; i >= 0; i--) {
      if (isAnimalOrBundle(cartData.items[i])) return cartData.items[i];
    }
    return null;
  }

  // Find the animal-specific sticker in an upsell list. Animals' upsell
  // lists typically contain exactly one non-DOU sticker — the matching one —
  // so any SKU containing "STICKER" qualifies once we exclude "Don't Open
  // Until..." stickers (which require a note). The upsell list ordering puts
  // the animal sticker before generic ones (e.g. mystery pals pack).
  function findAnimalSticker(upsells) {
    if (!upsells) return null;
    return upsells.find(p => {
      const sku = (p.sku || '').toUpperCase();
      if (sku.indexOf('DONT-OPEN') !== -1) return false;
      return sku.split('-').pop() === 'STICKER';
    });
  }

  function fetchDynamicUpsells(handle) {
    if (!handle) return Promise.resolve([]);
    if (dynamicUpsellsCache.has(handle)) {
      return dynamicUpsellsCache.get(handle);
    }
    // NOTE: intentionally no `Accept: application/json` header — Shopify
    // intercepts product URLs with that header and returns its built-in
    // product JSON API (ignoring ?view=), which has no `upsells` field.
    const promise = fetch('/products/' + encodeURIComponent(handle) + '?view=cart-upsells')
      .then(r => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(text => {
        const data = JSON.parse(text);
        return Array.isArray(data.upsells) ? data.upsells : [];
      });
    // Evict on failure so a transient error doesn't permanently disable
    // dynamic upsells for this handle (the next drawer open will retry).
    promise.catch(e => {
      console.error('Cart upsells: fetch failed for', handle, e);
      dynamicUpsellsCache.delete(handle);
    });
    dynamicUpsellsCache.set(handle, promise);
    // Surface failures as an empty list to callers so renderUpsells can
    // still render the static upsells without throwing.
    return promise.catch(() => []);
  }

  // State
  let cart = null;
  let isOpen = false;
  let previousActiveElement = null;
  let isNavigatingAway = false;

  function isNavigationalClick(event, link) {
    if (!link) return false;
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    if (!link.href) return false;
    return true;
  }

  /**
   * Announce a message to screen readers via the live region
   */
  function announceToSR(message) {
    const statusEl = document.getElementById('cart-drawer-status');
    if (statusEl) {
      statusEl.textContent = '';
      setTimeout(() => { statusEl.textContent = message; }, 50);
    }
  }

  /**
   * Format price in cents to currency string
   */
  function formatMoney(cents) {
    if (cents === 0) return 'FREE';
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\.00$/, '');
  }

  /**
   * Group cart items by _package_id
   * Returns { packages: [], otherItems: [] }
   */
  function groupItemsByPackage(items) {
    const packageMap = new Map();
    const otherItems = [];

    items.forEach((item, index) => {
      const packageId = item.properties && item.properties._package_id;
      if (packageId) {
        if (!packageMap.has(packageId)) {
          packageMap.set(packageId, { id: packageId, items: [] });
        }
        const pkg = packageMap.get(packageId);
        pkg.items.push({ ...item, lineIndex: index + 1 });
      } else {
        otherItems.push({ ...item, lineIndex: index + 1 });
      }
    });

    // Convert map to array and sort by package ID (descending - newest first)
    const packages = Array.from(packageMap.values()).sort((a, b) => {
      return parseInt(b.id) - parseInt(a.id);
    });

    return { packages, otherItems };
  }

  /**
   * Get the next available package ID
   */
  async function getNextPackageId() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();

      let maxId = 0;
      cartData.items.forEach(item => {
        const packageId = item.properties && item.properties._package_id;
        if (packageId) {
          const id = parseInt(packageId);
          if (!isNaN(id) && id > maxId) {
            maxId = id;
          }
        }
      });

      return maxId + 1;
    } catch (e) {
      console.error('Error getting next package ID:', e);
      return Date.now(); // Fallback to timestamp
    }
  }

  /**
   * Render inline message callout (for Personalized Card or Message properties)
   */
  function renderInlineMessage(message, propertyKey, lineIndex) {
    if (!message) return '';

    const escapedMessage = message
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    // Use data attribute to identify the property key for editing
    const dataKey = propertyKey === 'Personalized Card' ? 'notecard' : 'message';
    const displayLabel = propertyKey === 'Personalized Card' ? 'Personalized Card' : "Don't Open Until...";
    const charLimit = propertyKey === 'Personalized Card' ? 400 : 80;

    return `
      <div class="cart-drawer__inline-message" data-message-type="${dataKey}" data-line="${lineIndex}">
        <div class="cart-drawer__inline-message-header">
          <span class="cart-drawer__inline-message-label">${displayLabel}</span>
          <span class="cart-drawer__inline-message-counter"><span class="cart-drawer__inline-message-count">${message.length}</span> / ${charLimit}</span>
          <button type="button" class="cart-drawer__inline-message-edit" data-edit-message="${lineIndex}" data-property-key="${propertyKey}">Edit</button>
        </div>
        <p class="cart-drawer__inline-message-text">${escapedMessage}</p>
        <div class="cart-drawer__inline-message-edit-container">
          <p class="cart-drawer__inline-message-error" role="alert" aria-live="assertive"></p>
          <div class="cart-drawer__inline-message-textarea-container">
            <textarea class="cart-drawer__inline-message-textarea" maxlength="${charLimit}" data-property-key="${propertyKey}" data-char-limit="${charLimit}">${escapedMessage}</textarea>
          </div>
          <div class="cart-drawer__inline-message-actions">
            <button type="button" class="cart-drawer__inline-message-cancel" data-cancel-message="${lineIndex}">Cancel</button>
            <button type="button" class="cart-drawer__inline-message-save" data-save-message="${lineIndex}" data-property-key="${propertyKey}">Save</button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render a single cart item
   */
  function renderItem(item) {
    const isFree = item.final_price === 0 && item.original_price > 0;
    // Check for compare_at_price from line item property (stored when adding to cart)
    const compareAtPrice = item.properties && item.properties._compare_at_price ? parseInt(item.properties._compare_at_price) : 0;
    const hasCompareAtPrice = compareAtPrice > 0 && compareAtPrice > item.final_price;
    // Check for line-item discount (original_price > final_price)
    const hasLineDiscount = item.original_price !== item.final_price && !isFree;

    // Build visible properties (exclude hidden ones starting with _ and special properties)
    const visibleProperties = [];
    if (item.properties) {
      Object.entries(item.properties).forEach(([key, value]) => {
        // Exclude hidden props, Personalized Card, note, and Photo
        if (!key.startsWith('_') && key !== 'Personalized Card' && key !== 'note' && key !== 'Photo' && value) {
          visibleProperties.push({ key, value });
        }
      });
    }

    const propertiesHtml = visibleProperties.map(p =>
      `<span class="cart-drawer__item-property">${p.key}: ${p.value}</span>`
    ).join('');

    // Build price HTML - displayed below properties
    // Priority: compare_at_price > original_price > final_price
    let priceHtml = '';
    if (isFree) {
      priceHtml = `
        <span class="sr-only">Original price: </span><span class="cart-drawer__item-price--original">${formatMoney(item.original_price)}</span>
        <span class="sr-only">Current price: </span><span class="cart-drawer__item-price--free">FREE</span>
      `;
    } else if (hasCompareAtPrice) {
      // Show compare-at price as strikethrough
      priceHtml = `
        <span class="sr-only">Original price: </span><span class="cart-drawer__item-price--original">${formatMoney(compareAtPrice)}</span>
        <span class="sr-only">Current price: </span><span class="cart-drawer__item-price">${formatMoney(item.final_price)}</span>
      `;
    } else if (hasLineDiscount) {
      // Show original price as strikethrough (line-item discount)
      priceHtml = `
        <span class="sr-only">Original price: </span><span class="cart-drawer__item-price--original">${formatMoney(item.original_price)}</span>
        <span class="sr-only">Current price: </span><span class="cart-drawer__item-price">${formatMoney(item.final_price)}</span>
      `;
    } else {
      priceHtml = `<span class="cart-drawer__item-price">${formatMoney(item.final_price)}</span>`;
    }

    // Name any real line-level discount the item carries (e.g. the
    // "10% off w/ GiveAFriend" automatic discount), so the strikethrough has a
    // labelled reason. Only applied discounts populate item.discounts — the
    // promo overlay's cosmetic strikethrough does not — so this stays empty for
    // fake-sale lines.
    const discountTitles = (item.discounts || [])
      .map(d => d && d.title)
      .filter(Boolean);
    const discountLabelHtml = discountTitles.length
      ? `<span class="cart-drawer__item-discount">${discountTitles.join(', ')}</span>`
      : '';

    // SVG icons for quantity buttons
    const minusSvg = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 6H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;
    const plusSvg = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 2V10M2 6H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;

    // Build quantity pill HTML - disable plus for SIP and DOU (max qty 1)
    const isSIP = (item.sku || '') === 'BLUE-WRAPPING';
    const isDOU = (item.sku || '') === 'DONT-OPEN-STICKER';
    const maxQtyOne = isSIP || isDOU || isGafSubscription(item);
    // WCAG 2.4.6 / 1.3.1: every row used to expose the same three names
    // ("Decrease quantity" / "Quantity" / "Increase quantity"), so in a
    // multi-item cart a screen reader announces five identical control sets
    // with nothing tying them to a product. Name each control with the title
    // shown on the row — the same pattern the Care Package delete button
    // already uses. Titles are merchant-authored and routinely contain
    // quotes and ampersands, so run them through escapeHtml before they land
    // in an attribute.
    const itemLabelTitle = escapeHtml(item.product_title || item.title || 'item');
    const quantityHtml = `
      <div class="cart-drawer__quantity-pill" data-line="${item.lineIndex}">
        <button type="button" class="cart-drawer__quantity-btn" data-quantity-minus="${item.lineIndex}" aria-label="Decrease quantity of ${itemLabelTitle}">${minusSvg}</button>
        <input type="text" inputmode="numeric" pattern="[0-9]*" class="cart-drawer__quantity-input" data-quantity-input="${item.lineIndex}" value="${item.quantity}"${maxQtyOne ? ' readonly' : ''} aria-label="Quantity of ${itemLabelTitle}">
        <button type="button" class="cart-drawer__quantity-btn${maxQtyOne ? ' is-disabled' : ''}" data-quantity-plus="${item.lineIndex}" aria-label="Increase quantity of ${itemLabelTitle}"${maxQtyOne ? ' disabled' : ''}>${plusSvg}</button>
      </div>
    `;

    // Use uploaded photo if available (for Custom Framed Photo products).
    // When present, render the image inside a framed mockup wrapper so the
    // cart line item matches the PDP preview of the finished product.
    const uploadedPhoto = item.properties && item.properties.Photo;
    const imageUrl = uploadedPhoto || item.image || item.featured_image.url;
    const itemImageHtml = uploadedPhoto
      ? `<div class="cart-drawer__item-image-mockup"><img src="${imageUrl}" alt="${item.title}" loading="lazy"></div>`
      : `<img src="${imageUrl}" alt="${item.title}" class="cart-drawer__item-image" loading="lazy">`;

    // Check for inline messages (Personalized Card or note)
    const personalizedCard = item.properties && item.properties['Personalized Card'];
    const stickerMessage = item.properties && item.properties['note'];

    let inlineMessagesHtml = '';
    if (personalizedCard) {
      inlineMessagesHtml += renderInlineMessage(personalizedCard, 'Personalized Card', item.lineIndex);
    }
    if (stickerMessage) {
      inlineMessagesHtml += renderInlineMessage(stickerMessage, 'note', item.lineIndex);
    }

    return `
      <div class="cart-drawer__item" data-line="${item.lineIndex}" data-key="${item.key}" data-product-type="${(item.product_type || '').replace(/"/g, '&quot;')}">
        ${itemImageHtml}
        <div class="cart-drawer__item-details">
          <p class="cart-drawer__item-title">${item.product_title}</p>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>` : ''}
          ${item.selling_plan_allocation ? `<p class="cart-drawer__item-variant">${item.selling_plan_allocation.selling_plan.name}</p>` : ''}
          ${isGafSubscription(item) ? `<p class="cart-drawer__item-subscriber-badge">Unlocks $${item.properties[GAF_SUBSCRIPTION_PROPERTY]} off your order</p>` : ''}
          ${propertiesHtml ? `<div class="cart-drawer__item-properties">${propertiesHtml}</div>` : ''}
          <div class="cart-drawer__item-price-row">
            <div class="cart-drawer__item-price-wrap">
              ${priceHtml}
            </div>
            ${quantityHtml}
          </div>
          ${discountLabelHtml}
        </div>
        ${inlineMessagesHtml}
      </div>
    `;
  }

  /**
   * Render a care package
   */
  function renderPackage(pkg, displayIndex) {
    const itemsHtml = pkg.items.map(item => renderItem(item)).join('');

    return `
      <div class="cart-drawer__package" data-package-id="${pkg.id}">
        <header class="cart-drawer__package-header">
          <span class="cart-drawer__package-title">Care Package ${displayIndex}</span>
          <button type="button" class="cart-drawer__package-delete" data-delete-package="${pkg.id}" aria-label="Delete Care Package ${displayIndex}">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
          </button>
        </header>
        <div class="cart-drawer__package-items">
          ${itemsHtml}
        </div>
      </div>
    `;
  }

  /**
   * Render other items (without package ID)
   */
  function renderOtherItems(items) {
    if (!items.length) return '';

    const itemsHtml = items.map(item => renderItem(item)).join('');

    return `
      <div class="cart-drawer__other-items">
        <div class="cart-drawer__package-items">
          ${itemsHtml}
        </div>
      </div>
    `;
  }

  /**
   * Render upsell item
   */
  // Upsell prices are never previewed as discounted here: the GiveAFriend member
  // benefit is a flat amount off the order, taken once, so adding an upsell
  // doesn't make that upsell cheaper — the order total simply already has the
  // amount off it.
  function renderUpsellItem(product) {
    const trimmedDesc = (product.description || '').trim();
    const descriptionHTML = trimmedDesc ? `<p class="cart-drawer__upsell-description">${trimmedDesc}</p>` : '';
    return `
      <div class="cart-drawer__upsell-item" data-upsell-product-id="${product.id}" data-upsell-variant-id="${product.variantId}" data-product-type="${(product.productType || '').replace(/"/g, '&quot;')}">
        <img src="${product.image}" alt="${product.title}" class="cart-drawer__upsell-image" loading="lazy">
        <div class="cart-drawer__upsell-details">
          <p class="cart-drawer__upsell-title">${product.title}</p>
          ${descriptionHTML}
          <span class="cart-drawer__upsell-price">${formatMoney(product.price)}</span>
        </div>
        <button type="button" class="cart-drawer__upsell-add" data-add-upsell="${product.variantId}" data-compare-at-price="${product.compareAtPrice || 0}" ${!product.available ? 'disabled' : ''}>
          ${product.available ? 'Add' : 'Sold Out'}
        </button>
      </div>
    `;
  }

  /**
   * Render upsells section - filters out products already in cart.
   * Shows top N upsells from the most recently added animal/bundle's
   * pinned_upsells metafield, then the hardcoded upsells below.
   */
  async function renderUpsells(cartData) {
    if (!upsellsList) return;

    // Get product IDs and animal count from cart
    const cartProductIds = new Set();
    let animalCount = 0;
    if (cartData && cartData.items) {
      cartData.items.forEach(item => {
        cartProductIds.add(item.product_id);
        if (isAnimalOrBundle(item)) animalCount++;
      });
    }

    // Fetch dynamic upsells from the most recently added animal/bundle
    let dynamicUpsells = [];
    const recentItem = findMostRecentAnimalOrBundle(cartData);
    if (recentItem) {
      const handle = getItemHandle(recentItem);
      let all = await fetchDynamicUpsells(handle);
      // Promote the animal sticker to the top of the dynamic list so it
      // lands at position 2 overall (after SIP). It's a cheap, highly
      // relevant add-on for the animal the customer just added.
      const matchingSticker = findAnimalSticker(all);
      if (matchingSticker) {
        all = [matchingSticker].concat(all.filter(p => p.id !== matchingSticker.id));
      }
      dynamicUpsells = all
        .filter(p => p.available && !cartProductIds.has(p.id))
        .slice(0, DYNAMIC_UPSELL_LIMIT);
    }

    // Filter static upsells: exclude products in cart, already shown as dynamic,
    // and hide SIP unless exactly 1 animal. Split SIP out so we can render it
    // above the dynamic upsells (it's the most popular upsell).
    const dynamicIds = new Set(dynamicUpsells.map(p => p.id));
    const availableStatic = upsellProducts.filter(p => {
      if (cartProductIds.has(p.id)) return false;
      if (dynamicIds.has(p.id)) return false;
      if (animalCount !== 1 && p.sku === SIP_SKU) return false;
      return true;
    });
    const sipUpsells = availableStatic.filter(p => p.sku === SIP_SKU);
    const otherStatic = availableStatic.filter(p => p.sku !== SIP_SKU);

    // Stale-response guard: only render if this cart is still current.
    // (Another refresh may have overwritten `cart` while we awaited the fetch.)
    if (cart !== cartData) return;

    // Order: SIP (popular, conditional) → dynamic upsells → GAF/other static
    const combined = sipUpsells.concat(dynamicUpsells).concat(otherStatic);

    if (combined.length === 0) {
      if (upsellsContainer) {
        upsellsContainer.setAttribute('data-empty', 'true');
      }
      upsellsList.innerHTML = '';
      return;
    }

    if (upsellsContainer) {
      upsellsContainer.removeAttribute('data-empty');
    }

    upsellsList.innerHTML = combined.map(p => renderUpsellItem(p)).join('');

    // Bind add buttons
    bindUpsellEvents();
  }

  /**
   * Bind event listeners for upsell items
   */
  function bindUpsellEvents() {
    if (!upsellsList) return;

    upsellsList.querySelectorAll('[data-add-upsell]').forEach(btn => {
      btn.addEventListener('click', async function(e) {
        e.preventDefault();
        const variantId = parseInt(this.dataset.addUpsell);
        const itemEl = this.closest('.cart-drawer__upsell-item');
        await addUpsellToCart(variantId, itemEl, this);
      });
    });
  }

  /**
   * Add upsell item to cart with animation
   */
  async function addUpsellToCart(variantId, itemEl, btn) {
    if (!itemEl || !btn) return;

    // Show loading state
    btn.classList.add('is-loading');
    btn.disabled = true;
    itemEl.classList.add('is-adding');

    try {
      const compareAt = parseInt(btn.dataset.compareAtPrice) || 0;
      const itemData = { id: variantId, quantity: 1 };
      if (compareAt > 0) {
        itemData.properties = { '_compare_at_price': String(compareAt) };
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [itemData] })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      // Announce to screen readers
      announceToSR('Item added to cart');

      // Animate item out
      itemEl.classList.remove('is-adding');
      itemEl.classList.add('is-added');

      // Wait for animation then refresh cart
      setTimeout(async () => {
        itemEl.remove();

        // Check if upsells section is now empty
        const remainingItems = upsellsList.querySelectorAll('.cart-drawer__upsell-item');
        if (remainingItems.length === 0 && upsellsContainer) {
          upsellsContainer.setAttribute('data-empty', 'true');
        }

        // Refresh cart to show new item
        await refresh();
      }, 200);

    } catch (e) {
      console.error('Error adding upsell to cart:', e);
      announceToSR('Failed to add item to cart');
      btn.classList.remove('is-loading');
      btn.disabled = false;
      itemEl.classList.remove('is-adding');
    }
  }

  /**
   * Calculate totals and savings from cart items
   * Uses _compare_at_price property when available for "original" total
   * Returns { originalTotal, finalTotal, savings }
   */
  function calculateTotals(items) {
    let originalTotal = 0;
    let finalTotal = 0;

    items.forEach(item => {
      const qty = item.quantity || 1;
      // Get compare_at_price from line item property (stored when adding to cart)
      const compareAtPrice = item.properties && item.properties._compare_at_price ? parseInt(item.properties._compare_at_price) : 0;

      // Use compare_at_price from property if available, otherwise original_price
      const itemOriginalPrice = (compareAtPrice > 0 && compareAtPrice > item.final_price)
        ? compareAtPrice
        : item.original_price || item.final_price;
      originalTotal += itemOriginalPrice * qty;
      finalTotal += item.final_line_price || 0;
    });

    const savings = originalTotal - finalTotal;
    return { originalTotal, finalTotal, savings };
  }

  /**
   * Update the footer totals display
   */
  function updateTotals(cartData) {
    if (!cartData.items || cartData.items.length === 0) {
      totalEl.textContent = formatMoney(0);
      originalTotalEl.textContent = '';
      savingsEl.textContent = '';
      return;
    }

    const { originalTotal, finalTotal, savings } = calculateTotals(cartData.items);

    // Display items_subtotal_price (goods total after line-item/sale discounts
    // but before cart-level discount codes) rather than total_price. A discount
    // code the customer cached at checkout (e.g. SM20) is a cart-level discount
    // baked into total_price, so using total_price would (1) silently show a
    // lower subtotal with no discount UI to explain it, and (2) cause the
    // Discounted Price Display overlay — which reads this rendered amount and
    // applies its own % off — to double-count the discount. Fall back to
    // total_price for safety if items_subtotal_price is unavailable.
    const displaySubtotal = typeof cartData.items_subtotal_price === 'number'
      ? cartData.items_subtotal_price
      : cartData.total_price;
    totalEl.textContent = formatMoney(displaySubtotal);

    // Show strikethrough and savings if there's any discount
    if (savings > 0) {
      originalTotalEl.textContent = formatMoney(originalTotal);
      savingsEl.textContent = formatMoney(savings) + ' SAVINGS';
    } else {
      originalTotalEl.textContent = '';
      savingsEl.textContent = '';
    }
  }

  /**
   * Renumber care package titles after a package is removed
   */
  function renumberPackages() {
    const packages = content.querySelectorAll('.cart-drawer__package');
    packages.forEach((pkg, index) => {
      const titleEl = pkg.querySelector('.cart-drawer__package-title');
      if (titleEl) {
        titleEl.textContent = `Care Package ${index + 1}`;
      }
      const deleteBtn = pkg.querySelector('.cart-drawer__package-delete');
      if (deleteBtn) {
        deleteBtn.setAttribute('aria-label', `Delete Care Package ${index + 1}`);
      }
    });
  }

  /**
   * Check if cart is empty and show empty state
   */
  function checkEmptyState() {
    const hasItems = content.querySelector('.cart-drawer__item') !== null;
    drawer.setAttribute('data-empty', !hasItems);
    if (!hasItems) {
      content.innerHTML = '';
      totalEl.textContent = formatMoney(0);
      originalTotalEl.textContent = '';
      savingsEl.textContent = '';
    }
  }

  /* ----------------------------------------
     Cart Restrictions (JS-driven)
     ---------------------------------------- */

  const SIP_SKU = 'BLUE-WRAPPING';
  const DOU_SKU = 'DONT-OPEN-STICKER';
  const RESTRICTION_FADE_MS = 10000;

  // WCAG 2.2.1 (Timing Adjustable): the restriction banner used to fade on a
  // hard 10s timer with no way to stop, extend, or dismiss it — a shopper
  // reading slowly, or tabbing to the "Continue Shopping" link inside the
  // message, could lose it mid-read. The three helpers below give the timer an
  // off switch (the dismiss button), a pause (pointer or keyboard focus inside
  // the banner), and a restart on leave/blur. Every entry point clears
  // restrictionFadeTimer first so repeated messages can't stack timers.
  function clearRestrictionFade() {
    if (restrictionFadeTimer) {
      clearTimeout(restrictionFadeTimer);
      restrictionFadeTimer = null;
    }
  }

  function fadeOutRestrictionMsg(msgEl) {
    clearRestrictionFade();
    msgEl.style.transition = 'opacity 0.5s ease';
    msgEl.style.opacity = '0';
    setTimeout(() => msgEl.remove(), 500);
  }

  function startRestrictionFade(msgEl) {
    clearRestrictionFade();
    // Don't restart the countdown while the shopper is still reading it — a
    // mouseleave can fire while focus is parked on the link inside, and vice
    // versa, so both conditions are re-checked here rather than at the caller.
    if (!msgEl.isConnected) return;
    if (msgEl.matches(':hover')) return;
    if (msgEl.contains(document.activeElement)) return;
    restrictionFadeTimer = setTimeout(() => fadeOutRestrictionMsg(msgEl), RESTRICTION_FADE_MS);
  }

  function evaluateRestrictions(cartData) {
    if (!cartData || !cartData.items || cartData.items.length === 0) {
      clearRestrictionFade();
      if (restrictionsEl) restrictionsEl.innerHTML = '';
      if (checkoutBtnEl) checkoutBtnEl.style.display = '';
      return;
    }

    const items = cartData.items;
    let allowCheckout = false;
    let onlyUpsells = true;

    items.forEach(item => {
      const sku = item.sku || '';
      const ptype = item.product_type || '';

      if (ptype !== 'Upsell' && ptype !== 'Sticker') {
        onlyUpsells = false;
      }
      if (sku !== SIP_SKU && sku !== DOU_SKU) {
        allowCheckout = true;
      }
    });

    // Show/hide checkout button
    if (checkoutBtnEl) checkoutBtnEl.style.display = allowCheckout ? '' : 'none';

    // Render restriction messages
    let msgHtml = '';

    // Show pending SIP enforcement message with auto-fade. The message text
    // itself is untouched; only the dismiss control is appended after it.
    if (pendingRestrictionMsg) {
      msgHtml += '<p class="cart__no-checkout-msg cart__msg--fade">' + pendingRestrictionMsg +
        '<button type="button" class="cart-drawer__restriction-dismiss" data-dismiss-restriction aria-label="Dismiss this message">&times;</button></p>';
      pendingRestrictionMsg = null;
    }

    if (!allowCheckout) {
      msgHtml += '<p class="cart__no-checkout-msg">Your cart requires at least one physical item to checkout. <a class="link" href="/collections/shop-all">Continue Shopping</a></p>';
    } else if (onlyUpsells) {
      msgHtml += '<p class="cart__no-checkout-msg only_upsells"><span style="font-weight:700;">Did you forget a fluffy friend?</span> You only have accessories in your cart. If you want to send a complete care package, be sure to add a stuffed animal from our <a class="link" href="/collections/shop-all">collection of fluffy friends</a>!</p>';
    }

    if (restrictionsEl) {
      // Rewriting the region detaches whatever banner the in-flight timer was
      // pointed at, so retire it before the new markup lands.
      clearRestrictionFade();
      restrictionsEl.innerHTML = msgHtml;

      // Auto-fade pending messages, but let the shopper turn the timer off
      // (dismiss) or hold it open (hover / keyboard focus) — see WCAG 2.2.1
      // note above.
      const fadeMsg = restrictionsEl.querySelector('.cart__msg--fade');
      if (fadeMsg) {
        const dismissBtn = fadeMsg.querySelector('[data-dismiss-restriction]');
        if (dismissBtn) {
          dismissBtn.addEventListener('click', () => fadeOutRestrictionMsg(fadeMsg));
        }
        fadeMsg.addEventListener('mouseenter', clearRestrictionFade);
        fadeMsg.addEventListener('focusin', clearRestrictionFade);
        fadeMsg.addEventListener('mouseleave', () => startRestrictionFade(fadeMsg));
        fadeMsg.addEventListener('focusout', (e) => {
          // focusout fires before the next element takes focus, so
          // document.activeElement is unreliable here — relatedTarget tells us
          // whether focus is merely moving between the link and the dismiss
          // button inside the banner, which should not restart the countdown.
          if (e.relatedTarget && fadeMsg.contains(e.relatedTarget)) return;
          startRestrictionFade(fadeMsg);
        });
        startRestrictionFade(fadeMsg);
      }
    }
  }

  async function enforceSIP(cartData) {
    if (isEnforcing || !cartData || !cartData.items) return;

    const items = cartData.items;
    let animalCount = 0;
    let sipLine = null;
    let sipQuantity = 0;

    items.forEach((item, index) => {
      const sku = item.sku || '';
      const ptype = item.product_type || '';
      if (ptype.indexOf('Animal') !== -1 || ptype === 'Bundle') {
        animalCount++;
      }
      if (sku === SIP_SKU) {
        sipLine = index + 1;
        sipQuantity = item.quantity;
      }
    });

    if (sipLine === null) return;

    // SIP requires exactly 1 animal — remove if no animals or 2+ animals
    if (animalCount === 0) {
      isEnforcing = true;
      pendingRestrictionMsg = 'Safer in Paper requires a stuffed animal in your cart.';
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: sipLine, quantity: 0 })
        });
        await refresh();
      } finally {
        isEnforcing = false;
      }
      return;
    }

    // SIP + 2+ animals → remove SIP
    if (animalCount >= 2) {
      isEnforcing = true;
      pendingRestrictionMsg = 'We\u2019ve removed Safer in Paper since orders with two or more animals already come in an extra box.';
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: sipLine, quantity: 0 })
        });
        await refresh();
      } finally {
        isEnforcing = false;
      }
      return;
    }

    // SIP quantity > 1 → limit to 1
    if (sipQuantity > 1) {
      isEnforcing = true;
      pendingRestrictionMsg = 'Only one Safer in Paper can be added per order.';
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: sipLine, quantity: 1 })
        });
        await refresh();
      } finally {
        isEnforcing = false;
      }
      return;
    }
  }

  /**
   * Ungroup orphaned upsells (Upsell products in a package with no animal)
   */
  async function enforceOrphanedUpsells(cartData) {
    if (isEnforcing || !cartData || !cartData.items) return;

    // Group items by package
    const packages = new Map();
    cartData.items.forEach((item, index) => {
      const pkgId = item.properties && item.properties._package_id;
      if (!pkgId) return;
      if (!packages.has(pkgId)) packages.set(pkgId, []);
      packages.get(pkgId).push({ item, line: index + 1 });
    });

    // Find orphaned upsells: Upsell in a package that has no animal
    const orphanedLines = [];
    packages.forEach((pkgItems) => {
      const hasAnimal = pkgItems.some(({ item }) => {
        const ptype = item.product_type || '';
        return ptype.indexOf('Animal') !== -1 || ptype === 'Bundle';
      });
      if (hasAnimal) return;

      pkgItems.forEach(({ item, line }) => {
        if ((item.product_type || '') === 'Upsell') {
          orphanedLines.push({ line, item });
        }
      });
    });

    if (orphanedLines.length === 0) return;

    isEnforcing = true;
    try {
      for (const { line, item } of orphanedLines) {
        const updatedProperties = Object.assign({}, item.properties, { '_package_id': '' });
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity: item.quantity, properties: updatedProperties })
        });
      }
      await refresh();
    } finally {
      isEnforcing = false;
    }
  }

  /**
   * One GiveAFriend subscription per cart.
   *
   * The offer is $10 off the ORDER, so the discount app's rule only has to spot
   * a single line carrying `_gaf_subscription` — nothing is stamped on the other
   * lines. That makes the subscription line the whole trigger, so the one thing
   * worth policing is that exactly one of it exists: the PDP opt-in stays ticked
   * across adds, and a stale cached cart there can let a second subscription
   * through. Two lines would bill the shopper $30/month, not $15.
   */
  async function enforceSingleSubscription(cartData) {
    if (isEnforcing || !cartData || !cartData.items) return;

    let subLine = null;
    const duplicateSubLines = [];
    cartData.items.forEach(item => {
      if (!isGafSubscription(item)) return;
      if (subLine) duplicateSubLines.push(item);
      else subLine = item;
    });

    const fixes = [];
    duplicateSubLines.forEach(item => fixes.push({ id: item.key, quantity: 0 }));
    if (subLine && subLine.quantity > 1) {
      fixes.push({ id: subLine.key, quantity: 1 });
    }

    if (fixes.length === 0) return;

    isEnforcing = true;
    try {
      for (const fix of fixes) {
        // Line keys can go stale if an earlier change merges two lines; a failed
        // patch is picked up on the next render rather than aborting the batch.
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(fix)
          });
        } catch (e) {
          console.error('Error deduping GiveAFriend subscription:', e);
        }
      }
      await refresh();
    } finally {
      isEnforcing = false;
    }
  }

  /**
   * Render the entire cart
   */
  function renderCart(cartData) {
    cart = cartData;
    const isEmpty = !cartData.items || cartData.items.length === 0;

    drawer.setAttribute('data-empty', isEmpty);

    updateFreeShippingProgress(cartData);
    updateBuyMoreSaveMoreProgress(cartData);

    if (isEmpty) {
      content.innerHTML = '';
      totalEl.textContent = formatMoney(0);
      originalTotalEl.textContent = '';
      savingsEl.innerHTML = '';
      evaluateRestrictions(cartData);
      // Still render upsells when cart is empty
      renderUpsells(cartData);
      return;
    }

    const { packages, otherItems } = groupItemsByPackage(cartData.items);

    let html = '';

    // Render care packages (separation handled by CSS border-bottom)
    // Sequential numbering: newest package first = highest number
    packages.forEach((pkg, index) => {
      html += renderPackage(pkg, packages.length - index);
    });

    // Render other items
    if (otherItems.length) {
      html += renderOtherItems(otherItems);
    }

    content.innerHTML = html;

    // Update totals
    updateTotals(cartData);

    // Bind event listeners
    bindItemEvents();

    // Render upsells (filters out products already in cart)
    renderUpsells(cartData);

    // Evaluate restrictions and show/hide checkout
    evaluateRestrictions(cartData);

    // Enforce cart rules (may trigger re-render if violation found)
    runCartEnforcement();
  }

  /**
   * Membership wins: clear conflicting discount codes while a GiveAFriend
   * subscription is in the cart.
   *
   * The member discount (Every Possible Discount app, keyed on
   * `_gaf_subscription`) can't combine with the store's discount codes, which
   * are order-class — e.g. LOVELY has combinesWith.orderDiscounts = false. A code
   * left on the cart (commonly one entered at checkout on an earlier visit and
   * still stored on the session) fights the member discount, so on every render
   * Shopify re-picks a winner and the cart flickers / drops the discount.
   *
   * With a subscription in the cart we strip every applied code so the member
   * discount is the single source of truth. `discount: ''` clears all cart codes
   * via the Cart AJAX API; automatic/app discounts aren't codes and are left
   * alone. The signature guard means a clear that doesn't stick can't re-fire on
   * every render, and the guard re-arms once the cart is code-free so a code
   * added later still gets cleared.
   */
  async function enforceMembershipDiscountCodes(cartData) {
    if (isEnforcing || !cartData || !cartData.items) return;

    const hasSubscription = cartData.items.some(isGafSubscription);
    const codes = getAppliedDiscountCodes(cartData);

    if (!hasSubscription || codes.length === 0) {
      lastClearedCodeSig = null;
      return;
    }

    const signature = codes.slice().sort().join('|');
    if (signature === lastClearedCodeSig) return;
    lastClearedCodeSig = signature;

    isEnforcing = true;
    pendingRestrictionMsg =
      'Your GiveAFriend member discount was applied. ' + codes.join(', ') +
      ' can’t be combined with it, so we removed it.';
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' })
      });
      await refresh();
    } finally {
      isEnforcing = false;
    }
  }

  /**
   * Run the cart rules as one awaited sequence per render.
   *
   * Each enforcer holds the shared `isEnforcing` mutex only while it mutates the
   * cart, but SIP/orphan enforcement doesn't re-render once it settles. Running
   * them fire-and-forget meant that whenever an earlier rule acted, it held the
   * mutex through its own refresh re-render, so the last rule in the list got
   * skipped and never ran again. Awaiting them in order against the latest
   * `cart`, and skipping re-entry from an enforcer's own refresh, guarantees
   * every rule gets a turn.
   */
  async function runCartEnforcement() {
    if (isRunningCartRules) return;
    isRunningCartRules = true;
    try {
      await enforceSIP(cart);
      await enforceOrphanedUpsells(cart);
      await enforceMembershipDiscountCodes(cart);
      await enforceSingleSubscription(cart);
    } finally {
      isRunningCartRules = false;
    }
  }

  /**
   * Bind event listeners for items
   */
  function bindItemEvents() {
    // Delete package buttons
    content.querySelectorAll('[data-delete-package]').forEach(btn => {
      btn.addEventListener('click', async function(e) {
        e.preventDefault();
        const packageId = this.dataset.deletePackage;
        const packageEl = this.closest('.cart-drawer__package');
        await deletePackage(packageId, packageEl);
      });
    });

    // Edit inline message buttons (Personalized Card or Message)
    content.querySelectorAll('[data-edit-message]').forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        const lineIndex = this.dataset.editMessage;
        const callout = this.closest('.cart-drawer__inline-message');
        if (callout) {
          callout.classList.add('is-editing');
          drawer.classList.add('is-editing-notecard');
          const textarea = callout.querySelector('.cart-drawer__inline-message-textarea');
          if (textarea) {
            // Auto-resize to fit content before focusing
            autoResizeTextarea(textarea);
            textarea.focus();
            textarea.setSelectionRange(textarea.value.length, textarea.value.length);
          }
        }
      });
    });

    // Cancel inline message edit buttons
    content.querySelectorAll('[data-cancel-message]').forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        const callout = this.closest('.cart-drawer__inline-message');
        if (callout) {
          callout.classList.remove('is-editing');
          drawer.classList.remove('is-editing-notecard');
          // Reset textarea to original value
          const messageEl = callout.querySelector('.cart-drawer__inline-message-text');
          const textarea = callout.querySelector('.cart-drawer__inline-message-textarea');
          const errorEl = callout.querySelector('.cart-drawer__inline-message-error');
          if (messageEl && textarea) {
            textarea.value = messageEl.textContent;
            // Clear any error state
            textarea.classList.remove('cart-drawer__inline-message-textarea--error');
            if (errorEl) {
              errorEl.textContent = '';
              errorEl.classList.remove('is-visible');
            }
            updateInlineMessageCharCount(callout);
          }
        }
      });
    });

    // Save inline message buttons
    content.querySelectorAll('[data-save-message]').forEach(btn => {
      btn.addEventListener('click', async function(e) {
        e.preventDefault();
        const line = parseInt(this.dataset.saveMessage);
        const propertyKey = this.dataset.propertyKey;
        const callout = this.closest('.cart-drawer__inline-message');
        if (callout) {
          const textarea = callout.querySelector('.cart-drawer__inline-message-textarea');
          const errorEl = callout.querySelector('.cart-drawer__inline-message-error');
          if (textarea) {
            // Validate message
            const trimmedMsg = textarea.value.trim().replace(/^\s+|\s+$/g, '');
            const charLimit = parseInt(textarea.dataset.charLimit) || 400;
            let error = false;

            if (!trimmedMsg || trimmedMsg === '.') {
              error = propertyKey === 'Personalized Card'
                ? 'Please write a notecard before you continue.'
                : 'Please write a message before you continue.';
            } else if (textarea.value.length > charLimit) {
              error = `Message can't be longer than ${charLimit} characters`;
            }

            if (error) {
              textarea.classList.add('cart-drawer__inline-message-textarea--error');
              if (errorEl) {
                errorEl.textContent = error;
                errorEl.classList.add('is-visible');
              }
              textarea.focus();
              return;
            }

            await updateInlineMessage(line, propertyKey, trimmedMsg, callout);
          }
        }
      });
    });

    // Inline message textarea character counter
    content.querySelectorAll('.cart-drawer__inline-message-textarea').forEach(textarea => {
      textarea.addEventListener('input', function() {
        const callout = this.closest('.cart-drawer__inline-message');
        updateInlineMessageCharCount(callout);
      });
    });

    // Quantity minus buttons - quantity 0 removes item
    content.querySelectorAll('[data-quantity-minus]').forEach(btn => {
      btn.addEventListener('click', async function(e) {
        e.preventDefault();
        const line = parseInt(this.dataset.quantityMinus);
        const pill = this.closest('.cart-drawer__quantity-pill');
        const itemEl = this.closest('.cart-drawer__item');
        const input = pill.querySelector('.cart-drawer__quantity-input');
        const currentQty = parseInt(input.value) || 1;
        const newQty = currentQty - 1;
        if (newQty <= 0) {
          // Remove item when quantity goes to 0
          await removeItem(line, itemEl);
        } else {
          await updateQuantity(line, newQty, pill);
        }
      });
    });

    // Quantity plus buttons
    content.querySelectorAll('[data-quantity-plus]').forEach(btn => {
      btn.addEventListener('click', async function(e) {
        e.preventDefault();
        const line = parseInt(this.dataset.quantityPlus);
        const pill = this.closest('.cart-drawer__quantity-pill');
        const input = pill.querySelector('.cart-drawer__quantity-input');
        const currentQty = parseInt(input.value) || 1;
        await updateQuantity(line, currentQty + 1, pill);
      });
    });

    // Quantity input - direct editing
    content.querySelectorAll('[data-quantity-input]').forEach(input => {
      // Select all text on focus for easy replacement
      input.addEventListener('focus', function() {
        this.select();
      });

      // Update on blur
      input.addEventListener('blur', async function() {
        const line = parseInt(this.dataset.quantityInput);
        const pill = this.closest('.cart-drawer__quantity-pill');
        let newQty = parseInt(this.value) || 0;
        if (newQty < 0) newQty = 0;
        if (newQty === 0) {
          const itemEl = this.closest('.cart-drawer__item');
          await removeItem(line, itemEl);
        } else {
          await updateQuantity(line, newQty, pill);
        }
      });

      // Update on Enter key
      input.addEventListener('keydown', async function(e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.blur();
        }
      });
    });
  }

  /**
   * Auto-resize textarea to fit content (like PDP)
   */
  function autoResizeTextarea(textarea) {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  /**
   * Update character count display, auto-resize, and clear errors for inline messages
   */
  function updateInlineMessageCharCount(callout) {
    const textarea = callout.querySelector('.cart-drawer__inline-message-textarea');
    const countEl = callout.querySelector('.cart-drawer__inline-message-count');
    const counterEl = callout.querySelector('.cart-drawer__inline-message-counter');
    const errorEl = callout.querySelector('.cart-drawer__inline-message-error');

    if (textarea && countEl) {
      const charLimit = parseInt(textarea.dataset.charLimit) || 400;
      const warningThreshold = charLimit - 20;
      countEl.textContent = textarea.value.length;
      // Change color when near limit (like PDP)
      if (counterEl) {
        counterEl.style.color = textarea.value.length >= warningThreshold ? 'red' : 'rgb(132, 132, 132)';
      }
      // Clear error state when user types (like PDP)
      if (textarea.classList.contains('cart-drawer__inline-message-textarea--error')) {
        textarea.classList.remove('cart-drawer__inline-message-textarea--error');
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.classList.remove('is-visible');
        }
      }
    }
    // Auto-resize textarea
    autoResizeTextarea(textarea);
  }

  /**
   * Remove a single item by line number with animation
   */
  async function removeItem(line, itemEl) {
    if (!itemEl) return;

    // Add removing state (shows skeleton/fade)
    itemEl.classList.add('is-removing');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: 0 })
      });
      const cartData = await response.json();
      cart = cartData;

      // Animate item out
      itemEl.style.height = itemEl.offsetHeight + 'px';
      itemEl.offsetHeight; // Force reflow
      itemEl.classList.add('is-removed');

      // Wait for animation then do a full refresh to ensure upsells sync correctly
      setTimeout(async () => {
        await refresh();
      }, 200);

    } catch (e) {
      console.error('Error removing item:', e);
      itemEl.classList.remove('is-removing');
      await refresh();
    }
  }

  /**
   * Update quantity for a line item
   */
  async function updateQuantity(line, newQuantity, pillEl) {
    if (!pillEl) return;

    // Show updating state
    pillEl.classList.add('is-updating');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: newQuantity })
      });
      const cartData = await response.json();
      cart = cartData;

      // Refresh to update all displays correctly
      await refresh();

    } catch (e) {
      console.error('Error updating quantity:', e);
      pillEl.classList.remove('is-updating');
      await refresh();
    }
  }

  /**
   * Delete all items in a package with animation
   */
  async function deletePackage(packageId, packageEl) {
    if (!cart || !packageEl) return;

    // Add removing state to package
    packageEl.classList.add('is-removing');

    // Find all line items with this package ID
    const linesToRemove = [];
    cart.items.forEach((item, index) => {
      if (item.properties && item.properties._package_id === packageId) {
        linesToRemove.push(index + 1); // 1-based index
      }
    });

    // Remove items in reverse order to maintain correct indices
    linesToRemove.sort((a, b) => b - a);

    try {
      let cartData;
      for (const line of linesToRemove) {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: line, quantity: 0 })
        });
        cartData = await response.json();
      }

      cart = cartData;

      // Animate package out
      packageEl.style.height = packageEl.offsetHeight + 'px';
      packageEl.offsetHeight; // Force reflow
      packageEl.classList.add('is-removed');

      // Wait for animation then do a full refresh to ensure upsells sync correctly
      setTimeout(async () => {
        await refresh();
      }, 200);

    } catch (e) {
      console.error('Error deleting package:', e);
      packageEl.classList.remove('is-removing');
      await refresh();
    }
  }

  /**
   * Update inline message (Personalized Card or Message) for a line item
   */
  async function updateInlineMessage(line, propertyKey, message, callout) {
    if (!cart || line < 1 || line > cart.items.length || !callout) return;

    const item = cart.items[line - 1];
    const updatedProperties = { ...item.properties, [propertyKey]: message };

    // Show saving state
    const saveBtn = callout.querySelector('.cart-drawer__inline-message-save');
    const originalText = saveBtn ? saveBtn.textContent : 'Save';
    if (saveBtn) {
      saveBtn.textContent = 'Saving...';
      saveBtn.disabled = true;
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: line,
          quantity: item.quantity,
          properties: updatedProperties
        })
      });
      const cartData = await response.json();
      cart = cartData;

      // Update the message display inline
      const messageEl = callout.querySelector('.cart-drawer__inline-message-text');
      if (messageEl) {
        messageEl.textContent = message;
      }

      // Exit edit mode
      callout.classList.remove('is-editing');
      drawer.classList.remove('is-editing-notecard');

      // Reset save button
      if (saveBtn) {
        saveBtn.textContent = originalText;
        saveBtn.disabled = false;
      }

    } catch (e) {
      console.error('Error updating message:', e);
      if (saveBtn) {
        saveBtn.textContent = originalText;
        saveBtn.disabled = false;
      }
      // Show error to user
      const errorEl = callout.querySelector('.cart-drawer__inline-message-error');
      if (errorEl) {
        errorEl.textContent = 'Failed to save. Please try again.';
        errorEl.classList.add('is-visible');
      }
    }
  }

  /**
   * Show loading state (only used for initial load)
   */
  function showLoading() {
    content.innerHTML = `
      <div class="cart-drawer__loading" role="status" aria-label="Loading cart">
        <div class="cart-drawer__spinner"></div>
        <span class="sr-only">Loading cart...</span>
      </div>
    `;
  }

  /**
   * Update cart count in header
   */
  function updateCartCount(count) {
    const countEls = document.querySelectorAll('[data-js-cart-count]');
    countEls.forEach(el => {
      // Preserve the screen reader text
      const srText = el.querySelector('.sr-only');
      if (srText) {
        el.innerHTML = count + srText.outerHTML;
      } else {
        el.textContent = count;
      }
    });

    // Show/hide cart indicator
    const indicators = document.querySelectorAll('[data-js-cart-indicator]');
    indicators.forEach(el => {
      if (count > 0) {
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
  }

  /* ----------------------------------------
     Free Shipping Progress Bar
     ---------------------------------------- */

  // Always re-parse the JSON config block so that edits in the theme editor
  // (which re-render the sale-promo-config section) are picked up without
  // relying on a cached window.__activePromo value from a prior run.
  function getActivePromo() {
    const configEl = document.getElementById('sale-promo-config');
    if (!configEl) return null;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return null;
    }

    const promos = config.promos || [];
    if (!promos.length) return null;

    const previewPromo = promos.find(p => p.previewMode);
    if (previewPromo) return previewPromo;

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter.formatToParts(new Date());
    const get = type => parts.find(p => p.type === type).value;
    const today = `${get('year')}-${get('month')}-${get('day')}`;

    return promos.find(p =>
      p.startDate && p.endDate && today >= p.startDate && today <= p.endDate
    ) || null;
  }

  function formatProgressAmount(cents) {
    const dollars = cents / 100;
    return dollars % 1 === 0
      ? '$' + dollars.toFixed(0)
      : '$' + dollars.toFixed(2);
  }

  // Goods total BEFORE any discount — the number Shopify evaluates a discount's
  // minimum-purchase condition against, and therefore the only number the
  // progress bars can measure. `items_subtotal_price` is NOT it: Shopify
  // allocates an automatic *product* discount (which is how Buy More Save More
  // is set up) down onto the line items, so that field already has the discount
  // subtracted. Measuring against it makes a $50 cart that just earned 15% off
  // read as $42.50 and claim the shopper is still $7.50 short of the tier they
  // already unlocked — the bar fights the discount it's advertising.
  function getGoodsSubtotal(cartData) {
    if (!cartData) return 0;
    if (typeof cartData.original_total_price === 'number') {
      return cartData.original_total_price;
    }
    if (Array.isArray(cartData.items)) {
      return cartData.items.reduce(function(sum, item) {
        return sum + (item.original_line_price || item.final_line_price || 0);
      }, 0);
    }
    return typeof cartData.total_price === 'number' ? cartData.total_price : 0;
  }

  // Escape HTML-unsafe characters so values substituted into richtext
  // templates can't break out of attributes or inject markup.
  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function(c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function updateFreeShippingProgress(cartData) {
    const progressEl = document.getElementById('cart-drawer-progress');
    if (!progressEl) return;

    const promo = getActivePromo();
    // `showCart` intentionally ignored here: in free shipping mode the
    // progress bar replaces the cart banner, so the toggle doesn't apply.
    // Buy More Save More mode renders its own tiered bar and takes precedence,
    // so bail out (and leave the checkout button alone) when it's enabled.
    if (!promo || !promo.freeShippingMode || promo.buyMoreSaveMoreMode) {
      progressEl.hidden = true;
      return;
    }

    const thresholdDollars = Number(promo.freeShippingThreshold);
    if (!thresholdDollars || thresholdDollars <= 0) {
      progressEl.hidden = true;
      return;
    }

    const isEmpty = !cartData
      || !cartData.items
      || cartData.items.length === 0
      || cartData.item_count === 0;
    if (isEmpty) {
      progressEl.hidden = true;
      return;
    }

    const thresholdCents = Math.round(thresholdDollars * 100);
    // Pre-discount goods total, so the bar tracks the same number Shopify's
    // discount-minimum condition evaluates against.
    const subtotal = getGoodsSubtotal(cartData);
    const remaining = Math.max(0, thresholdCents - subtotal);
    const percent = Math.min(100, Math.max(0, (subtotal / thresholdCents) * 100));
    const unlocked = subtotal >= thresholdCents;

    const messageEl = document.getElementById('cart-drawer-progress-message');
    const trackEl = document.getElementById('cart-drawer-progress-track');
    const fillEl = document.getElementById('cart-drawer-progress-fill');
    const minEl = document.getElementById('cart-drawer-progress-min');
    const maxEl = document.getElementById('cart-drawer-progress-max');

    if (minEl) {
      minEl.textContent = '$0';
      minEl.style.color = promo.progressBarTextColor || '';
    }
    if (maxEl) {
      maxEl.textContent = formatProgressAmount(thresholdCents);
      maxEl.style.color = promo.progressBarTextColor || '';
    }

    if (messageEl) {
      const progressTemplate = promo.freeShippingProgressLabel
        || "<p>You're {amount} away from FREE shipping!</p>";
      const unlockedTemplate = promo.freeShippingUnlockedLabel
        || "<p>🎉 You've unlocked FREE shipping!</p>";
      // Richtext is admin-controlled HTML, but substituted values may not
      // be, so escape them before injecting into innerHTML. Use global
      // replace so duplicated placeholders all get filled.
      const codeHtml = escapeHtml(promo.promoCode || '');
      const amountHtml = escapeHtml(formatProgressAmount(remaining));
      const html = unlocked
        ? unlockedTemplate.replace(/\{code\}/g, codeHtml)
        : progressTemplate
            .replace(/\{amount\}/g, amountHtml)
            .replace(/\{code\}/g, codeHtml);
      messageEl.innerHTML = html;
      messageEl.style.color = promo.progressBarTextColor || '';
    }

    if (fillEl) {
      fillEl.style.width = percent + '%';
      fillEl.style.background = promo.progressBarFillColor || '';
    }

    if (trackEl) {
      trackEl.style.background = promo.progressBarTrackColor || '';
    }

    // Container chrome mirrors the original PDP/cart sale banner: dashed
    // border in the promo's border color, the promo background color, and
    // the promo banner font color cascading to text via `color: inherit`.
    progressEl.style.backgroundColor = promo.bgColor || '';
    progressEl.style.borderColor = promo.borderColor || '';
    progressEl.style.color = promo.bannerFontColor || '';

    // Re-query the checkout button each call so we don't hold a stale
    // reference if the header section (which contains the cart drawer
    // snippet) was reloaded in the theme editor.
    const checkoutBtnNow = document.getElementById('cart-drawer-checkout');
    if (checkoutBtnNow) {
      if (unlocked && promo.promoCode) {
        // Shopify's /discount/<code>?redirect=/checkout endpoint sets the
        // discount cookie server-side before forwarding to checkout, which
        // auto-applies the code. ?discount= on /checkout is unreliable on
        // newer checkouts.
        checkoutBtnNow.href = '/discount/' + encodeURIComponent(promo.promoCode) + '?redirect=/checkout';
      } else {
        checkoutBtnNow.href = '/checkout';
      }
    }

    progressEl.hidden = false;
  }

  // Buy More Save More: one progress bar with several tier markers that nudge
  // the shopper toward the next discount (e.g. 10% off $40 → 15% off $50 →
  // 20% off $60). The real discount is configured in Shopify as automatic
  // discounts and applies itself at checkout, so this bar is informational
  // only — it never rewrites the checkout button to a /discount/<code> link,
  // which keeps it from stacking on top of (double-counting) that discount.
  function updateBuyMoreSaveMoreProgress(cartData) {
    const progressEl = document.getElementById('cart-drawer-bmsm');
    if (!progressEl) return;
    // Reset the flag; only the success path at the end re-enables it. This
    // lets the cart header tighten its bottom padding while the bar shows.
    drawer.removeAttribute('data-bmsm-active');

    const promo = getActivePromo();
    if (!promo || !promo.buyMoreSaveMoreMode) {
      progressEl.hidden = true;
      return;
    }

    // Normalize tiers: dollars → cents, drop blanks, sort ascending.
    const rawTiers = Array.isArray(promo.buyMoreSaveMoreTiers) ? promo.buyMoreSaveMoreTiers : [];
    const tiers = rawTiers
      .map(function(t) {
        return { cents: Math.round(Number(t.threshold) * 100), label: (t.label || '').toString() };
      })
      .filter(function(t) { return t.cents > 0; })
      .sort(function(a, b) { return a.cents - b.cents; });
    if (!tiers.length) {
      progressEl.hidden = true;
      return;
    }

    const isEmpty = !cartData
      || !cartData.items
      || cartData.items.length === 0
      || cartData.item_count === 0;
    if (isEmpty) {
      progressEl.hidden = true;
      return;
    }

    // Measure against the pre-discount goods total so the tiers line up with
    // the subtotal minimums Shopify evaluates. Crossing a tier discounts the
    // line items, which would otherwise drag the measured subtotal back below
    // the threshold that earned it.
    const subtotal = getGoodsSubtotal(cartData);

    const n = tiers.length;
    const segment = 100 / n;

    // Highest reached tier and the next tier still ahead (if any).
    let currentIndex = -1;
    let nextIndex = -1;
    for (let i = 0; i < n; i++) {
      if (subtotal >= tiers[i].cents) {
        currentIndex = i;
      } else {
        nextIndex = i;
        break;
      }
    }

    // Markers are spaced evenly (not by dollar value) so close thresholds like
    // $40/$50/$60 don't bunch up. The fill animates evenly between each pair of
    // consecutive thresholds (0 → t0 → t1 → …).
    let fillPercent;
    if (nextIndex === -1) {
      fillPercent = 100;
    } else {
      const lower = nextIndex === 0 ? 0 : tiers[nextIndex - 1].cents;
      const upper = tiers[nextIndex].cents;
      const within = upper > lower ? (subtotal - lower) / (upper - lower) : 0;
      fillPercent = (nextIndex + within) * segment;
    }
    fillPercent = Math.min(100, Math.max(0, fillPercent));

    const markersEl = document.getElementById('cart-drawer-bmsm-markers');
    if (markersEl) {
      let html = '';
      // Inline SVG check keeps the mark optically centered in the dot; the
      // unicode ✓ glyph sits high and to the right in some fonts.
      const checkSvg = '<svg class="cart-drawer__tier-check" viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
        '<path d="M4.5 8.5 7 11 11.5 5.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      for (let i = 0; i < n; i++) {
        const pos = (i + 1) * segment;
        const reached = subtotal >= tiers[i].cents;
        let edgeClass = '';
        if (pos >= 90) edgeClass = ' is-edge-right';
        else if (pos <= 10) edgeClass = ' is-edge-left';
        const labelText = tiers[i].label ? escapeHtml(tiers[i].label) : '';
        // Combine the label and threshold onto one line (e.g. "10% off $40+")
        // so each marker is a single short line and the bar stays compact.
        const amountText = escapeHtml(formatProgressAmount(tiers[i].cents)) + '+';
        const labelInner = labelText ? (labelText + ' ' + amountText) : amountText;
        html +=
          '<div class="cart-drawer__tier-marker' + (reached ? ' is-reached' : '') + edgeClass + '" style="left:' + pos + '%">' +
            '<span class="cart-drawer__tier-dot" aria-hidden="true">' + (reached ? checkSvg : '') + '</span>' +
            '<span class="cart-drawer__tier-label">' + labelInner + '</span>' +
          '</div>';
      }
      markersEl.innerHTML = html;
    }

    const messageEl = document.getElementById('cart-drawer-bmsm-message');
    if (messageEl) {
      const progressTemplate = promo.buyMoreSaveMoreProgressLabel
        || "<p>You're {amount} away from {nextLabel}!</p>";
      const unlockedTemplate = promo.buyMoreSaveMoreUnlockedLabel
        || "<p>🎉 You've unlocked our biggest discount — {currentLabel}!</p>";
      // Richtext is admin-controlled, but substituted values may not be, so
      // escape them before injecting. Global replace fills duplicated tokens.
      const currentLabelHtml = currentIndex >= 0 ? escapeHtml(tiers[currentIndex].label) : '';
      let html;
      if (nextIndex === -1) {
        html = unlockedTemplate
          .replace(/\{currentLabel\}/g, escapeHtml(tiers[n - 1].label));
      } else {
        const amountHtml = escapeHtml(formatProgressAmount(tiers[nextIndex].cents - subtotal));
        const nextLabelHtml = escapeHtml(tiers[nextIndex].label);
        html = progressTemplate
          .replace(/\{amount\}/g, amountHtml)
          .replace(/\{nextLabel\}/g, nextLabelHtml)
          .replace(/\{currentLabel\}/g, currentLabelHtml);
      }
      messageEl.innerHTML = html;
      messageEl.style.color = promo.progressBarTextColor || '';
    }

    const fillEl = document.getElementById('cart-drawer-bmsm-fill');
    if (fillEl) {
      fillEl.style.width = fillPercent + '%';
      fillEl.style.background = promo.progressBarFillColor || '';
    }

    // The track stays a fixed neutral (set in CSS) so the unfilled portion is
    // always visible against the drawer's white background, regardless of the
    // promo's near-white track color.

    // Reached markers and all labels pick up the configured fill/text colors;
    // unreached markers keep the neutral CSS defaults.
    if (markersEl) {
      if (promo.progressBarFillColor) {
        markersEl.querySelectorAll('.cart-drawer__tier-marker.is-reached .cart-drawer__tier-dot').forEach(function(dot) {
          dot.style.background = promo.progressBarFillColor;
          dot.style.borderColor = promo.progressBarFillColor;
        });
      }
      markersEl.querySelectorAll('.cart-drawer__tier-label').forEach(function(lbl) {
        lbl.style.color = promo.progressBarTextColor || '';
      });
    }

    // Automatic-discount setup: the discount applies itself at checkout with no
    // code, so leave the checkout button as a plain /checkout. Reset it in case
    // a prior mode (e.g. free shipping) left a /discount/<code> link behind.
    const checkoutBtnNow = document.getElementById('cart-drawer-checkout');
    if (checkoutBtnNow) checkoutBtnNow.href = '/checkout';

    progressEl.hidden = false;
    drawer.setAttribute('data-bmsm-active', 'true');
  }

  /**
   * Fetch cart and render
   */
  async function refresh() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      renderCart(cartData);
      updateCartCount(cartData.item_count);
      announceToSR('Cart updated, ' + cartData.item_count + ' ' + (cartData.item_count === 1 ? 'item' : 'items') + ' in cart');
    } catch (e) {
      console.error('Error fetching cart:', e);
    }
  }

  /**
   * Open the cart drawer
   */
  function open() {
    if (isNavigatingAway) return;

    previousActiveElement = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    isOpen = true;

    // Focus close button
    const closeBtn = drawer.querySelector('.cart-drawer__close');
    if (closeBtn) {
      setTimeout(() => closeBtn.focus(), 100);
    }

    // Fetch latest cart data
    showLoading();
    refresh();
  }

  /**
   * Close the cart drawer
   */
  function close() {
    if (!isOpen) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    isOpen = false;

    // On /cart shell, navigate to homepage when closing the drawer
    if (window.__cartShellMode === true) {
      window.location.href = '/';
      return;
    }

    if (previousActiveElement) {
      previousActiveElement.focus();
    }
  }

  // Event Listeners

  // Close drawer buttons
  drawer.querySelectorAll('[data-close-drawer]').forEach(el => {
    el.addEventListener('click', close);
  });

  // Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && isOpen) {
      close();
    }
  });

  // Focus trap
  drawer.addEventListener('keydown', function(e) {
    if (e.key !== 'Tab' || !isOpen) return;

    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusableElements = drawer.querySelectorAll(focusableSelector);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  });

  // Header cart icon click
  document.addEventListener('click', function(e) {
    const link = e.target.closest('a[href]');
    if (isNavigationalClick(e, link) && !link.hasAttribute('data-open-cart-drawer')) {
      isNavigatingAway = true;
    }

    const cartLink = e.target.closest('[data-open-cart-drawer]');
    if (cartLink) {
      e.preventDefault();
      isNavigatingAway = false;
      open();
    }
  }, true);

  window.addEventListener('beforeunload', function() {
    isNavigatingAway = true;
  });

  window.addEventListener('pagehide', function() {
    isNavigatingAway = true;
  });

  // Re-render the progress bar when the theme editor reloads any section
  // (e.g. the Sale Promo Config block). The cart drawer snippet itself may
  // also be re-injected if a parent section reloads, so we defer to the next
  // tick to ensure fresh DOM nodes exist before we read/update them.
  document.addEventListener('shopify:section:load', function() {
    setTimeout(function() {
      updateFreeShippingProgress(cart);
      updateBuyMoreSaveMoreProgress(cart);
    }, 0);
  });

  // Expose public API
  window.CartDrawer = {
    open: open,
    close: close,
    refresh: refresh,
    getNextPackageId: getNextPackageId,
    // Last-rendered cart. Add-to-cart reads it to avoid stacking a second
    // GiveAFriend subscription when one is already in the cart.
    getCart: function () { return cart; }
  };

})();
